import React, { useState } from "react";
import { Link } from "react-router-dom";

// ─────────────────────────────────────────────
//  Project: Weather App with API
//  Chapter 10 · Projects
//  Author: Ajao Hazzan · Full-Stack Software Engineer
// ─────────────────────────────────────────────

const PRIMARY = "#61DAFB";
const BG = "https://images.unsplash.com/photo-1555066931-4365d14bab8c?w=1600&q=80";

/* ── Code snippets stored as template literals ── */
const CODE_1 = `# .env  (never commit this file)
VITE_WEATHER_API_KEY=your_api_key_here
VITE_WEATHER_BASE=https://api.openweathermap.org/data/2.5`;

const CODE_2 = `// pages/WeatherApp.jsx
import { useState, useCallback } from 'react';
import { useLocalStorage } from '../hooks/useLocalStorage';
import { useGeolocation } from '../hooks/useGeolocation';

export default function WeatherApp() {
  const [city, setCity]       = useState('');
  const [weather, setWeather] = useState(null);
  const [forecast, setForecast] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');
  const [unit, setUnit]       = useLocalStorage('unit', 'metric');
  const [history, setHistory] = useLocalStorage('searches', []);

  const API_KEY  = import.meta.env.VITE_WEATHER_API_KEY;
  const BASE_URL = import.meta.env.VITE_WEATHER_BASE;

  const fetchWeather = useCallback(async (searchCity) => {
    if (!searchCity?.trim()) return;
    setLoading(true);
    setError('');
    try {
      const [weatherRes, forecastRes] = await Promise.all([
        fetch(\`\${BASE_URL}/weather?q=\${searchCity}&units=\${unit}&appid=\${API_KEY}\`),
        fetch(\`\${BASE_URL}/forecast?q=\${searchCity}&units=\${unit}&cnt=5&appid=\${API_KEY}\`),
      ]);
      if (!weatherRes.ok) throw new Error('City not found');
      const [weatherData, forecastData] = await Promise.all([
        weatherRes.json(), forecastRes.json()
      ]);
      setWeather(weatherData);
      setForecast(forecastData.list);
      // Add to history (max 5 recent cities)
      setHistory(prev => [searchCity, ...prev.filter(c => c !== searchCity)].slice(0, 5));
    } catch (err) {
      setError(err.message);
      setWeather(null);
    } finally {
      setLoading(false);
    }
  }, [unit]);

  const degSymbol = unit === 'metric' ? '°C' : '°F';

  return (
    <div className="weather-app">
      <h1>🌤 Weather App</h1>

      <div className="controls">
        <input
          value={city}
          onChange={e => setCity(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && fetchWeather(city)}
          placeholder="Search city..."
        />
        <button onClick={() => fetchWeather(city)} disabled={loading}>
          {loading ? '⏳' : '🔍'}
        </button>
        <button onClick={() => setUnit(u => u === 'metric' ? 'imperial' : 'metric')}>
          {unit === 'metric' ? '°F' : '°C'}
        </button>
      </div>

      {/* Recent searches */}
      {history.length > 0 && (
        <div className="history">
          {history.map(h => (
            <button key={h} onClick={() => { setCity(h); fetchWeather(h); }}>
              {h}
            </button>
          ))}
        </div>
      )}

      {error && <p className="error">❌ {error}</p>}

      {weather && (
        <div className="weather-card">
          <h2>{weather.name}, {weather.sys.country}</h2>
          <img
            src={\`https://openweathermap.org/img/wn/\${weather.weather[0].icon}@2x.png\`}
            alt={weather.weather[0].description}
          />
          <p className="temp">{Math.round(weather.main.temp)}{degSymbol}</p>
          <p>{weather.weather[0].description}</p>
          <div className="details">
            <span>💧 {weather.main.humidity}%</span>
            <span>💨 {weather.wind.speed} m/s</span>
            <span>👁 {weather.visibility / 1000} km</span>
          </div>
        </div>
      )}
    </div>
  );
}`;


/* ── Styles ── */
const styles = {
  page:    { minHeight: "100vh", backgroundImage: `linear-gradient(rgba(10,12,30,0.94),rgba(10,12,30,0.97)),url(${BG})`, backgroundSize: "cover", backgroundAttachment: "fixed", color: "#fff", fontFamily: "'Segoe UI',sans-serif", paddingBottom: "60px" },
  header:  { background: "rgba(10,12,30,0.95)", borderBottom: "1px solid rgba(97,218,251,0.15)", padding: "18px 32px", display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: "12px", position: "sticky", top: 0, zIndex: 100 },
  badge:   { background: "rgba(97,218,251,0.12)", border: "1px solid rgba(97,218,251,0.3)", color: PRIMARY, padding: "4px 14px", borderRadius: "999px", fontSize: "0.75rem", fontWeight: 700, letterSpacing: "0.06em" },
  backBtn: { background: "rgba(97,218,251,0.1)", border: "1px solid rgba(97,218,251,0.25)", color: PRIMARY, padding: "8px 18px", borderRadius: "999px", cursor: "pointer", fontSize: "0.82rem", fontWeight: 600, textDecoration: "none", display: "inline-flex", alignItems: "center", gap: "6px" },
  hero:    { padding: "48px 32px 24px", maxWidth: "900px", margin: "0 auto" },
  title:   { fontSize: "clamp(1.6rem,4vw,2.4rem)", fontWeight: 800, color: PRIMARY, marginBottom: "10px", lineHeight: 1.2 },
  desc:    { color: "rgba(255,255,255,0.55)", fontSize: "1rem", lineHeight: 1.7, maxWidth: "680px", marginBottom: "32px" },
  content: { maxWidth: "900px", margin: "0 auto", padding: "0 32px 40px" },
  section: { marginBottom: "36px" },
  sh:      { fontSize: "1.1rem", fontWeight: 700, color: "#fff", marginBottom: "10px", borderLeft: "3px solid " + PRIMARY, paddingLeft: "12px" },
  body:    { color: "rgba(255,255,255,0.72)", lineHeight: 1.85, fontSize: "0.95rem", marginBottom: "6px" },
  pre:     { background: "#0D0F1A", border: "1px solid rgba(97,218,251,0.15)", borderLeft: "3px solid " + PRIMARY, borderRadius: "10px", padding: "20px 22px", overflowX: "auto", margin: "14px 0", fontSize: "0.83rem", lineHeight: 1.7, color: "#CDD6F4", fontFamily: "Courier New,monospace", whiteSpace: "pre" },
  qBox:    { background: "rgba(97,218,251,0.04)", border: "1px solid rgba(97,218,251,0.12)", borderRadius: "10px", padding: "18px 22px", marginTop: "8px" },
  qTitle:  { color: PRIMARY, fontWeight: 700, fontSize: "0.82rem", letterSpacing: "0.08em", marginBottom: "10px" },
  qItem:   { color: "rgba(255,255,255,0.65)", lineHeight: 1.8, fontSize: "0.92rem", marginBottom: "4px" },
  footer:  { maxWidth: "900px", margin: "0 auto", padding: "24px 32px 0", borderTop: "1px solid rgba(255,255,255,0.07)", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "12px" },
  credit:  { color: "rgba(255,255,255,0.25)", fontSize: "0.75rem" },
};

/* ── Component ── */
export default function ProjectWeatherApp() {
  const [copied, setCopied] = useState(null);

  function copyCode(codeStr, idx) {
    navigator.clipboard?.writeText(codeStr).then(() => {
      setCopied(idx);
      setTimeout(() => setCopied(null), 2000);
    });
  }

  return (
    <div style={styles.page}>

      {/* Header */}
      <header style={styles.header}>
        <div style={{ display: "flex", alignItems: "center", gap: "16px", flexWrap: "wrap" }}>
          <Link to="/react-dashboard" style={styles.backBtn}>← Dashboard</Link>
          <span style={styles.badge}>Chapter 10 · Projects</span>
        </div>
        <span style={{ color: "rgba(255,255,255,0.3)", fontSize: "0.78rem" }}>
          By <strong style={{ color: PRIMARY }}>Ajao Hazzan</strong> · Full-Stack Software Engineer
        </span>
      </header>

      {/* Hero */}
      <div style={styles.hero}>
        <h1 style={styles.title}>Project: Weather App with API</h1>
        <p style={styles.desc}>Build a weather application that integrates with a live weather API.</p>
      </div>

      {/* Content */}
      <div style={styles.content}>

      /* ── Project Overview ── */
      <div style={styles.section}>
        <h2 style={styles.sh}>Project Overview</h2>
        <p style={styles.body} dangerouslySetInnerHTML={{ __html: "A fully functional weather app:" }} />
        <p style={styles.body} dangerouslySetInnerHTML={{ __html: "• Search any city and get current weather" }} />
        <p style={styles.body} dangerouslySetInnerHTML={{ __html: "• 5-day forecast cards" }} />
        <p style={styles.body} dangerouslySetInnerHTML={{ __html: "• Toggle Celsius/Fahrenheit" }} />
        <p style={styles.body} dangerouslySetInnerHTML={{ __html: "• Geolocation (detect user&#39;s location)" }} />
        <p style={styles.body} dangerouslySetInnerHTML={{ __html: "• Recent searches history" }} />
        <p style={styles.body} dangerouslySetInnerHTML={{ __html: "• Weather condition icons" }} />
      </div>

      /* ── API Setup ── */
      <div style={styles.section}>
        <h2 style={styles.sh}>API Setup</h2>
        <p style={styles.body} dangerouslySetInnerHTML={{ __html: "Register for a free API key at openweathermap.org. Store it in .env." }} />
        <div style={{ position: 'relative' }}>
          <button
            onClick={() => copyCode(CODE_1, 0)}
            style={{
              position: 'absolute', top: '10px', right: '10px',
              background: 'rgba(97,218,251,0.15)',
              border: '1px solid rgba(97,218,251,0.3)',
              color: PRIMARY, borderRadius: '6px',
              padding: '4px 10px', cursor: 'pointer',
              fontSize: '0.72rem', fontWeight: 700,
            }}
          >
            {copied === 0 ? '✓ Copied' : 'Copy'}
          </button>
          <pre style={styles.pre}>{String(CODE_1)}</pre>
        </div>
      </div>

      /* ── Complete WeatherApp Component ── */
      <div style={styles.section}>
        <h2 style={styles.sh}>Complete WeatherApp Component</h2>
        <div style={{ position: 'relative' }}>
          <button
            onClick={() => copyCode(CODE_2, 1)}
            style={{
              position: 'absolute', top: '10px', right: '10px',
              background: 'rgba(97,218,251,0.15)',
              border: '1px solid rgba(97,218,251,0.3)',
              color: PRIMARY, borderRadius: '6px',
              padding: '4px 10px', cursor: 'pointer',
              fontSize: '0.72rem', fontWeight: 700,
            }}
          >
            {copied === 1 ? '✓ Copied' : 'Copy'}
          </button>
          <pre style={styles.pre}>{String(CODE_2)}</pre>
        </div>
      </div>

      /* ── Extension Challenges ── */
      <div style={styles.section}>
        <h2 style={styles.sh}>Extension Challenges</h2>
        <div style={styles.qBox}>
          <div style={styles.qTitle}>📝 EXTENSION CHALLENGES</div>
          <div style={styles.qItem}>✦ 1. Add a map showing the city\'s location (use Leaflet.js)</div>
          <div style={styles.qItem}>✦ 2. Add hourly forecast with a line chart (use Recharts)</div>
          <div style={styles.qItem}>✦ 3. Add a dark/light theme toggle</div>
          <div style={styles.qItem}>✦ 4. Cache API responses for 10 minutes to reduce API calls</div>
          <div style={styles.qItem}>✦ 5. Add weather-based background images (sunny = bright, rainy = dark)</div>
        </div>
      </div>

        {/* Footer */}
        <div style={styles.footer}>
          <Link to="/react-dashboard" style={styles.backBtn}>← Back to React Dashboard</Link>
          <span style={styles.credit}>Mastering React · © 2024 Ajao Hazzan</span>
        </div>
      </div>

    </div>
  );
}
